/**
 * **브라우저 SDK 의 공개 API 셋** — `track` · `trackPageView` · `identify`.
 *
 * ```
 *   화면 hooks/handler   track({ name: 'item_opened', type: 'action', props: {…} }, { uiId })
 *     ▼
 *   이 파일             봉투 조립(신원 · WS · 화면 · 버전 · 세션 첫 이벤트의 referrer/utm)
 *     ▼ queue           묶어서 POST /api/analytics/collect
 * ```
 *
 * `track` 은 `AnalyticsEvent` 만 받는다 — 이름·props 가 정본과 다르면 컴파일 오류다.
 * 화면 맥락(uid · workspace · path)은 `<AnalyticsProvider>`(react.tsx)가 `setAnalyticsContext` 로 넣는다.
 * SSR 에서는 전부 no-op 이다.
 */

import type {
  IsoDate,
  AnalyticsPage,
  AnalyticsEvent,
  AnalyticsClientContext,
  AnalyticsClientEnvelope,
} from 'src/types/analytics/events';

import { CONFIG } from 'src/global-config';

import { AnalyticsQueue } from './queue';
import { pageOfPath } from './page-of-path';
import { noteServerAppVersion } from './version-skew';
import { newId, getAnonymousId, touchSessionId } from './session';

// ----------------------------------------------------------------------

export type AnalyticsContext = {
  uid: string | null;
  workspaceId: string | null;
  path: string;
  experimentId?: string | null;
  variant?: string | null;
};

export type TrackOptions = {
  /** `화면.영역.요소` — 컴포넌트의 `data-ui-id` */
  uiId?: string | null;
};

type State = {
  ctx: AnalyticsContext;
  queue: AnalyticsQueue | null;
  /** 직전 세션 id — 바뀌면 «세션 첫 이벤트»라 referrer·utm 을 싣는다 */
  lastSessionId: string | null;
  /** 직전에 `user_identified` 를 보낸 uid — 같은 uid 로 두 번 보내지 않는다 */
  identifiedUid: string | null;
  lastPageViewPath: string | null;
};

const state: State = {
  ctx: { uid: null, workspaceId: null, path: '' },
  queue: null,
  lastSessionId: null,
  identifiedUid: null,
  lastPageViewPath: null,
};

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

function queue(): AnalyticsQueue {
  if (!state.queue) {
    state.queue = new AnalyticsQueue({
      onDropped: (reason, count) => track({ name: 'events_dropped', type: 'system', props: { reason, count } }),
      onServerVersion: (version) => noteServerAppVersion(version, CONFIG.appVersion),
    });
  }
  return state.queue;
}

/** Provider 가 부른다 — uid·WS·경로가 바뀔 때마다 */
export function setAnalyticsContext(next: Partial<AnalyticsContext>): void {
  state.ctx = { ...state.ctx, ...next };
}

/** 검색 요청 하나를 잇는 id — `search_submitted` 와 `search_completed` 가 같은 값을 쓴다 */
export function newRequestId(): string {
  return newId();
}

/** dayjs·Date 류를 `YYYY-MM-DD` 로 — 봉투에는 Date 객체를 싣지 않는다. 없으면 `undefined` */
export function toIsoDate(value: { format: (f: string) => string } | Date | null | undefined): IsoDate | undefined {
  if (!value) return undefined;
  const text = value instanceof Date ? value.toISOString().slice(0, 10) : value.format('YYYY-MM-DD');
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? (text as IsoDate) : undefined;
}

/** 지금 보고 있는 WS — Provider 가 넣은 값. 앱 로그가 「어느 고객의 오류인가」를 채우는 데 쓴다 */
export function currentWorkspaceId(): string | null {
  return state.ctx.workspaceId;
}

/** 화면 id — 경로에서 유도한다(정본은 feature-map 의 routes) */
export function currentPage(): AnalyticsPage | null {
  return pageOfPath(state.ctx.path);
}

/** 이벤트 하나를 남긴다. 실패해도 던지지 않는다 — 로그가 화면을 막으면 안 된다 */
export function track(event: AnalyticsEvent, options: TrackOptions = {}): void {
  if (!isBrowser()) return;
  try {
    queue().enqueue(buildEnvelope(event, options));
  } catch {
    // 조립 실패는 삼킨다 — 봉투 한 줄보다 화면이 먼저다
  }
}

/** 경로가 바뀔 때만 `page_viewed` 하나. 같은 경로 재렌더는 무시한다 */
export function trackPageView(path: string): void {
  if (!isBrowser() || !path || state.lastPageViewPath === path) return;
  state.lastPageViewPath = path;
  setAnalyticsContext({ path });
  track({ name: 'page_viewed', type: 'page', props: {} });
}

/** 로그인 확인 — 게스트 → 사용자 연결을 `user_identified` 한 줄로 남긴다(uid 당 한 번) */
export function identify(uid: string | null): void {
  setAnalyticsContext({ uid });
  if (!isBrowser() || !uid || state.identifiedUid === uid) return;
  state.identifiedUid = uid;
  track({ name: 'user_identified', type: 'identify', props: { anonymous_id: getAnonymousId() } });
}

/** 언로드 직전 — Provider 가 `pagehide` 에 부른다 */
export function flushAnalyticsOnUnload(): void {
  if (!isBrowser() || !state.queue) return;
  const beacon =
    typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function'
      ? (url: string, data: Blob) => navigator.sendBeacon(url, data)
      : null;
  state.queue.flushWithBeacon(beacon);
}

/** 테스트용 — 상태를 처음으로 */
export function resetAnalyticsForTests(): void {
  state.ctx = { uid: null, workspaceId: null, path: '' };
  state.queue = null;
  state.lastSessionId = null;
  state.identifiedUid = null;
  state.lastPageViewPath = null;
}

// ---- private ------------------------------------------------------------

function buildEnvelope(event: AnalyticsEvent, options: TrackOptions): AnalyticsClientEnvelope {
  const sessionId = touchSessionId(state.ctx.uid);
  const sessionFirst = sessionId !== state.lastSessionId;
  state.lastSessionId = sessionId;

  const context: AnalyticsClientContext = {
    event_id: newId(),
    schema_version: 2,
    client_ts: new Date().toISOString(),
    anonymous_id: getAnonymousId(),
    session_id: sessionId,
    workspace_id: state.ctx.workspaceId,
    app_version: CONFIG.appVersion,
    path: state.ctx.path,
    page: pageOfPath(state.ctx.path),
    ui_id: options.uiId ?? null,
    experiment_id: state.ctx.experimentId ?? null,
    variant: state.ctx.variant ?? null,
    ...(sessionFirst ? sessionEntry() : {}),
  };
  return { ...context, ...event };
}

/** 세션 첫 이벤트에만 싣는 유입 정보 */
function sessionEntry(): Pick<AnalyticsClientContext, 'referrer' | 'utm_source' | 'utm_medium' | 'utm_campaign'> {
  const referrer = typeof document !== 'undefined' ? document.referrer : '';
  const params = new URLSearchParams(window.location.search);
  const pick = (k: string) => params.get(k) ?? undefined;
  return {
    referrer: referrer || undefined,
    utm_source: pick('utm_source'),
    utm_medium: pick('utm_medium'),
    utm_campaign: pick('utm_campaign'),
  };
}
